import type { Run } from "../hooks/useProethicsFlowStore";
import type { AvatarRole, TranscriptMessage, TurnEvent } from "../types";

export type TurnCtx = {
	avatarId: string;
	avatarRole: AvatarRole;
	isCurrentTurn: boolean;
	isActiveScenario: boolean;
};

export type TurnEventResult = {
	patch: Partial<Run>;
	startAudioTimer?: boolean;
	clearAudioTimer?: boolean;
};

const NO_OP: TurnEventResult = { patch: {} };

/** Creates the avatar bubble lazily on the first reply token (no empty placeholder), born `pending`; otherwise patches it. */
const upsertAvatar = (
	transcript: TranscriptMessage[],
	ctx: TurnCtx,
	patch: Partial<TranscriptMessage>,
): TranscriptMessage[] => {
	if (transcript.some((m) => m.id === ctx.avatarId)) {
		return transcript.map((m) =>
			m.id === ctx.avatarId ? { ...m, ...patch } : m,
		);
	}
	return [
		...transcript,
		{
			id: ctx.avatarId,
			role: ctx.avatarRole,
			text: "",
			audioStatus: "pending",
			...patch,
		},
	];
};

const findAvatar = (run: Run, ctx: TurnCtx) =>
	run.transcript.find((m) => m.id === ctx.avatarId);

// The newest reply always wins: it autoplays even if an earlier clip is still sounding, and that clip is
// preempted when this one claims playback (see `usePreemptedByOther`).
const shouldAutoplay = (run: Run, ctx: TurnCtx): boolean =>
	ctx.isCurrentTurn &&
	ctx.isActiveScenario &&
	run.transcript[run.transcript.length - 1]?.id === ctx.avatarId;

const failAudio = (run: Run, ctx: TurnCtx): TurnEventResult => {
	if (findAvatar(run, ctx)?.audioStatus !== "pending") {
		return NO_OP;
	}
	return {
		patch: {
			transcript: upsertAvatar(run.transcript, ctx, {
				audioStatus: "unavailable",
			}),
		},
		clearAudioTimer: true,
	};
};

/** Pure state machine; the caller (hook) owns all side effects — network, timers, chunk buffering. */
export const reduceTurnEvent = (
	run: Run,
	event: TurnEvent,
	ctx: TurnCtx,
): TurnEventResult => {
	switch (event.type) {
		case "data-thread-created":
			return { patch: { threadId: event.data.threadId } };

		case "data-avatar-reply":
			return {
				patch: {
					transcript: upsertAvatar(run.transcript, ctx, {
						text: event.data.reply,
					}),
				},
			};

		case "data-turn-state":
			// Reply text is complete; unlock the UI now, TTS continues after.
			return {
				patch: {
					arcStage: event.data.arcStage,
					...(ctx.isCurrentTurn ? { isStreaming: false } : {}),
				},
				startAudioTimer: true,
			};

		case "audio-assembled": {
			if (!event.data.audioBase64) {
				return failAudio(run, ctx);
			}
			const transcript = upsertAvatar(run.transcript, ctx, {
				audioBase64: event.data.audioBase64,
				audioStatus: "ready",
			});
			return {
				patch: {
					transcript,
					...(shouldAutoplay(run, ctx)
						? { autoplayMessageId: ctx.avatarId }
						: {}),
				},
				clearAudioTimer: true,
			};
		}

		case "data-avatar-audio-url":
			return {
				patch: {
					transcript: upsertAvatar(run.transcript, ctx, {
						audioUrl: event.data.url,
					}),
				},
			};

		case "data-hint":
			return ctx.isCurrentTurn
				? { patch: { currentHint: event.data.hint, hintPending: false } }
				: NO_OP;

		case "audio-timeout":
		case "audio-aborted":
		case "stream-closed": {
			const result = failAudio(run, ctx);
			if (ctx.isCurrentTurn && run.hintPending) {
				return { ...result, patch: { ...result.patch, hintPending: false } };
			}
			return result;
		}

		default:
			// data-avatar-audio-chunk is buffered by the hook; not turn state.
			return NO_OP;
	}
};
